export function formatDate(date) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

export function formatDateShort(date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export function toISODateString(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function fromISODateString(str) {
  const [y, m, d] = str.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function addDays(date, days) {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

export const GRID_START_HOUR = 5
export const GRID_END_HOUR = 24 // midnight, stored as 00:00
export const DEFAULT_START_TIME = '08:00'
export const DEFAULT_END_TIME = '09:00'

export function parseHour(t) {
  return parseInt(String(t || '00:00').split(':')[0], 10) || 0
}

export function formatTime12(timeStr) {
  if (!timeStr) return ''
  const [hourStr, minStr] = timeStr.split(':')
  const hour = parseInt(hourStr)
  const min = minStr || '00'
  const ampm = hour < 12 ? 'AM' : 'PM'
  const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${h}:${min} ${ampm}`
}

export function addOneHour(timeStr) {
  if (!timeStr) return ''
  const [h, m] = timeStr.split(':').map(Number)
  const newH = Math.min(h + 1, 23)
  return `${String(newH).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Strip Postgres seconds (`08:00:00` → `08:00`) so selects and comparisons match. */
export function normalizeTime(t) {
  if (t == null || t === '') return ''
  const [hourStr, minStr] = String(t).trim().split(':')
  const hour = parseInt(hourStr, 10)
  if (Number.isNaN(hour)) return ''
  const minutes = parseInt(minStr || '00', 10)
  const min = Number.isNaN(minutes) ? 0 : minutes
  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

/** Midnight (`00:00`) is end-of-day (hour 24), not start-of-day. */
export function calendarHour(t) {
  const h = parseHour(t)
  return h === 0 ? GRID_END_HOUR : h
}

export function buildTimeSlot(hour) {
  const ampm = hour === 24 ? 'AM' : hour < 12 ? 'AM' : 'PM'
  const displayHour = hour === 24 ? 12 : hour <= 12 ? hour : hour - 12
  // PostgreSQL normalizes time '24:00' → '00:00', so use '00:00' for midnight
  const value = hour === 24 ? '00:00' : `${String(hour).padStart(2, '0')}:00`
  return {
    label: `${displayHour}:00 ${ampm}`,
    value,
    hour,
  }
}

export function buildHourSlots(startHour, endHour) {
  const slots = []
  for (let hour = startHour; hour <= endHour; hour++) {
    slots.push(buildTimeSlot(hour))
  }
  return slots
}

/**
 * Default grid is 5am–midnight. If any job starts earlier (e.g. 1am),
 * prepend those hours so the job is visible and editable. Midnight stays last.
 */
export function getVisibleTimeSlots(jobs = []) {
  let minHour = GRID_START_HOUR
  for (const job of jobs || []) {
    const start = calendarHour(job.start_time)
    if (start < minHour) minHour = start
  }
  if (minHour < 1) minHour = 1
  return buildHourSlots(minHour, GRID_END_HOUR)
}

export function isOperatingStartTime(t) {
  const h = calendarHour(t)
  return h >= GRID_START_HOUR && h <= GRID_END_HOUR
}

/** TIME_SLOTS plus the current value when editing an off-hours job (1am, etc.). */
export function getTimeSelectOptions(currentTime, operatingSlots) {
  const base = operatingSlots || buildHourSlots(GRID_START_HOUR, GRID_END_HOUR)
  const norm = normalizeTime(currentTime)
  if (!norm || base.some(s => s.value === norm)) return base
  return [buildTimeSlot(calendarHour(norm)), ...base]
}

/**
 * Empty / invalid times become 08:00–09:00 — never 01:00.
 * Existing off-hours times are kept so a date-only edit can recover the job.
 */
export function resolveSavedTimes(start_time, end_time, { existingOffHours = false } = {}) {
  const start = normalizeTime(start_time)
  if (!start) {
    return { start_time: DEFAULT_START_TIME, end_time: DEFAULT_END_TIME }
  }
  if (!isOperatingStartTime(start) && !existingOffHours) {
    return { start_time: DEFAULT_START_TIME, end_time: DEFAULT_END_TIME }
  }
  const end = normalizeTime(end_time) || addOneHour(start)
  return { start_time: start, end_time: end }
}
